import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HourglassEmptyOutlined } from '@mui/icons-material';
import { Avatar } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMeeting } from '../../../../../hooks/useMeeting';
import { Booth, Meetingroom } from '../../../../../interfaces/interfaces';
import { setBooths } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../services/helper';
import { fileUpload, getBooth, updateMeeting } from '../../../../../services/services';
import MeetingRoomControlButtons from '../../../../account/Booth/BoothInfo/MeetingRoomControlButtons';
import VyTooltip from '../../../../common/VyTooltip';
import { VyUploadCrop } from '../../../../common/VyUploadCrop';
import InlineEditTextField from '../../../pageBuilder/InlineEditTextField';

interface MeetingRoomTemplateEditProps {
  meetingroom: Meetingroom;
  hideJoinButton?: boolean;
  booth: Booth;
  onRefresh?: () => void;
}

const MeetingRoomTemplateEdit = ({ meetingroom, booth, onRefresh, hideJoinButton }: MeetingRoomTemplateEditProps) => {
  const displaystatus = useSelector(({ common }) => common.meetingroomStatuses[meetingroom._id]);
  const event = useSelector(({ common }) => common.event);
  const booths = useSelector(({ common }) => common.booths);

  const [name, setName] = useState(meetingroom.contactperson);
  const [position, setPosition] = useState(meetingroom.contactposition);
  const [fieldsLoading, setFieldsLoading] = useState<string[]>([]);

  const { t } = useTranslation(['booth', 'common']);
  const { join } = useMeeting();
  const dispatch = useDispatch();

  const status = displaystatus || meetingroom.displaystatus;

  const refreshBooth = async () => {
    const _booth = await getBooth({ boothid: booth._id });
    dispatch(setBooths(booths.map((b) => (b._id === _booth._id ? _booth : b))));
  };

  const uploadImage = async (b: Blob) => {
    const data = await fileUpload(b);
    await updateMeeting({ meetingroomid: meetingroom.id, image: data.data.fileId });
    await refreshBooth();
  };

  const updateMeetingProp = async (path: string, value: string) => {
    setFieldsLoading((prevFieldsLoading) => [...prevFieldsLoading, path]);
    await updateMeeting({ meetingroomid: meetingroom.id, [path]: value });
    await refreshBooth();
    setFieldsLoading((prevFieldsLoading) => prevFieldsLoading.filter((e) => e !== path));
  };

  return (
    <div className="flex flex-col items-center w-full gap-2 sm:flex-row" id={meetingroom?._id}>
      <VyUploadCrop cropShape="round" onImage={uploadImage}>
        <div className="relative flex items-center justify-center cursor-pointer">
          <div className="absolute z-20 p-2 bg-black bg-opacity-50 rounded-full">
            <FontAwesomeIcon className="text-white" style={{ height: '15px', width: '20px', cursor: 'pointer' }} icon={faUpload} />
          </div>
          <div style={{ filter: status === 'offline' ? 'grayscale(100%)' : 'none', minWidth: '64px', position: 'relative' }}>
            <Avatar
              className="object-cover object-center w-16 h-16 border border-black rounded-full"
              src={meetingroom.image ? createDisplayLink(meetingroom.image) : '/avatar.png'}
              alt="meetingroom image"
            />
          </div>
        </div>
      </VyUploadCrop>
      <div className="flex flex-col justify-between w-full h-full">
        <div className="flex flex-col justify-start h-full ml-4 space-y-2" style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>
          <div className="flex">
            <InlineEditTextField
              name="contactperson"
              fullWidth
              className="p-1 pr-10 user-input name-input"
              inputProps={{ maxLength: 80 }}
              defaultValue={meetingroom.contactperson}
              label={t('booth:name-consultant')}
              onChange={(e) => setName(e.target.value)}
              saveButtonProps={{
                hidden: name === meetingroom.contactperson,
                disabled: fieldsLoading.includes('contactperson'),
                onClick: () => updateMeetingProp('contactperson', name),
              }}
              loading={fieldsLoading.includes('contactperson')}
            />
            {event?.settings?.showQueueIndicator && status === 'queue' && (
              <VyTooltip title={t('booth:has-queue')}>
                <HourglassEmptyOutlined fontSize="small" className="ml-1 text-red-600 animate-pulse" />
              </VyTooltip>
            )}
          </div>
          <InlineEditTextField
            name="contactposition"
            fullWidth
            className="p-1 pr-10 user-input name-input"
            inputProps={{ maxLength: 80 }}
            defaultValue={meetingroom.contactposition}
            label={t('booth:position-consultant')}
            onChange={(e) => setPosition(e.target.value)}
            loading={fieldsLoading.includes('contactposition')}
            saveButtonProps={{
              hidden: position === meetingroom.contactposition,
              disabled: fieldsLoading.includes('contactposition'),
              onClick: () => updateMeetingProp('contactposition', position),
            }}
          />
        </div>
        {hideJoinButton !== true && (
          <button
            onClick={() => join(meetingroom, booth, status, true, true)}
            className="px-4 py-1 ml-4 text-xs rounded bg-buttons-main templateBorder"
            data-cy="join-button">
            {t('common:join')}
          </button>
        )}
      </div>
      <MeetingRoomControlButtons booth={booth} meetingroom={meetingroom} />
    </div>
  );
};

export default MeetingRoomTemplateEdit;
